/**
 * Centralized size mapping constants for consistent scaling across components
 * These are shared by Button, Icon, and Links components to maintain a single source of truth
 */

/**
 * Button size multipliers for padding scaling
 * Base size (medium) = 1, others scale proportionally
 * Applied to --button-padding-v and --button-padding-h CSS variables
 */
export const BUTTON_SIZE_MULTIPLIERS = {
  xxsmall: 0.5,
  xsmall: 0.65,
  small: 0.75,
  medium: 1, // Base size
  large: 1.25,
  xlarge: 1.5,
  xxlarge: 1.75,
} as const;

/**
 * Button text size mapping to CSS variables
 * Allows independent control of text size from button padding
 */
export const BUTTON_TEXT_SIZE_MAP = {
  xxsmall: 'calc(var(--body-tiny-size) * 0.75)',
  xsmall: 'calc(var(--body-tiny-size))',
  small: 'var(--body-small-size)',
  medium: 'var(--body-base-size)',
  large: 'var(--body-large-size)',
  xlarge: 'calc(var(--body-large-size) * 1.5)',
  xxlarge: 'calc(var(--body-large-size) * 1.75)',
} as const;

/**
 * Icon size mapping relative to --button-icon-size CSS variable
 * Provides consistent icon sizing across the application
 */
export const ICON_SIZE_MAP = {
  xxsmall: 'calc(var(--button-icon-size) * 0.6)',
  xsmall: 'calc(var(--button-icon-size) * 0.75)',
  small: 'calc(var(--button-icon-size) * 0.9)',
  medium: 'var(--button-icon-size)',
  large: 'calc(var(--button-icon-size) * 1.2)',
  xlarge: 'calc(var(--button-icon-size) * 1.5)',
  xxlarge: 'calc(var(--button-icon-size) * 1.8)',
} as const;

/**
 * Spacing gap sizes for layout components
 * Maps semantic size names to CSS spacing variables
 */
export const SPACING_GAP_MAP = {
  xsmall: 'var(--spacing-xsmall)',
  small: 'var(--spacing-small)',
  base: 'var(--spacing-base)',
  large: 'var(--spacing-large)',
  xlarge: 'var(--spacing-xlarge)',
} as const;

// Type exports for TypeScript
export type ButtonSize = keyof typeof BUTTON_SIZE_MULTIPLIERS;
export type TextSize = keyof typeof BUTTON_TEXT_SIZE_MAP;
export type IconSize = keyof typeof ICON_SIZE_MAP;
export type GapSize = keyof typeof SPACING_GAP_MAP;
